
import { useEffect } from "react";
import { Link } from "react-router-dom";
import CTAButton from "@/components/CTAButton";
import { Check, Plus, ArrowRight } from "lucide-react";

const Continue = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isFirefox = userAgent.includes('firefox');
  const isEdge = userAgent.includes('edg/');

  useEffect(() => {
    document.title = "Continue - Search Safe";
  }, []);

  const openStore = () => {
    const url = "https://chromewebstore.google.com/detail/pdf-search-helper/bficdbgmiiecnmoldiffehkhcafobnkl";
    const width = window.screen.width;
    const height = window.screen.height;
    window.open(url, "ExtensionStore", `width=${width},height=${height},left=0,top=0`);
  };

  const getBrowserName = () => {
    if (isFirefox) return "Firefox";
    if (isEdge) return "Edge";
    return "Chrome";
  };

  return <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 py-4 px-6">
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/522d17fa-541b-46b2-aec6-aec8edf40b37.png" 
              alt="PDF Search Helper" 
              className="h-12" 
              loading="eager"
            />
          </Link>
        </div>
      </header>
      
      <div className="flex-1 max-w-5xl mx-auto px-4 py-12 w-full">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold mb-5">Continue to Download</h1>
          <p className="text-gray-700 max-w-2xl mx-auto text-sm">
            Install the free <strong>Search Safe</strong> extension for Google Chrome™. This extension provides <strong>SSL-secured web searches</strong> with advanced encryption, protecting your privacy while highlighting non-secure search results.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 mb-8 max-w-4xl mx-auto">
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <div className="min-w-4 mt-1">•</div>
              <p className="text-gray-700 font-normal text-sm">
                <strong>SSL-encrypted search queries</strong> - Your search data is protected with advanced encryption technology.
              </p>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="min-w-4 mt-1">•</div>
              <p className="text-gray-700 text-sm">
                <strong>Real-time security threat detection</strong> - Get instant protection from malicious websites and unsafe search results.
              </p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <div className="min-w-4 mt-1">•</div>
              <p className="text-gray-700 text-sm">
                <strong>Privacy-focused search results</strong> - Your searches remain private and secure with our advanced protection.
              </p>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="min-w-4 mt-1">•</div>
              <p className="text-gray-700 text-sm">
                Get Search Safe safely and easily from the Chrome Web Store.
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center mb-8">
          <CTAButton 
            {...(isFirefox 
              ? { href: "https://addons.mozilla.org/firefox/downloads/file/4529261/pdf_search_helper-1.0.0.xpi" }
              : { onClick: openStore }
            )}
            size="large"
            className="bg-green-500 hover:bg-green-600 text-white text-xl px-14 py-6 rounded-full"
          >
            {isFirefox ? "Download Now" : "Add to Chrome"}
          </CTAButton>
        </div>
        
        <div className="text-center text-sm text-gray-600 max-w-2xl mx-auto mb-20">
          <p className="text-xs">
            This free extension will update this browser's search settings. By continuing, you acknowledge the 
            <a href="https://system1.com/terms/privacy-policy" target="_blank" className="text-blue-600 mx-1">Privacy Policy</a>.
          </p>
        </div>
      </div>
      
      <div className="w-full bg-gray-700 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8 text-white">Install in 3 Easy Steps</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center flex flex-col items-center">
              <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mb-4">
                <ArrowRight className="text-white" />
              </div>
              <h3 className="font-bold text-white">Step 1:</h3>
              <p className="text-gray-300">Click 'Add to Chrome' above</p>
            </div>
            
            <div className="text-center flex flex-col items-center">
              <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mb-4">
                <Plus className="text-white" />
              </div>
              <h3 className="font-bold text-white">Step 2:</h3>
              <p className="text-gray-300">Safely Add Extension in the Chrome Web Store</p>
            </div>
            
            <div className="text-center flex flex-col items-center">
              <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mb-4">
                <Check className="text-white" />
              </div>
              <h3 className="font-bold text-white">Step 3:</h3>
              <p className="text-gray-300">Start Searching!</p>
            </div>
          </div>
        </div>
      </div>
      
      <footer className="py-8 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex justify-center gap-8">
            <a href="https://system1.com/terms/privacy-policy" target="_blank" className="text-sm text-gray-500 hover:text-gray-700">Privacy Policy</a>
            <Link to="/uninstall" className="text-sm text-gray-500 hover:text-gray-700">Uninstall</Link>
          </div>
        </div>
      </footer>
    </div>;
};

export default Continue;
